import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Menu from '@menu/components/menu';
import Spinner from '@menu/components/shared/spinner';
import { maxMobileWidth } from '@menu/core/constants';
import Inbox, { inboxCache } from '@menu/core/inbox';
import getCountryAndCulture from '@menu/core/lib/get-country-and-culture';
import getSiteAndCulture from '@menu/core/lib/get-site-and-culture';
import MenuContext, { MenuContextOptions } from '@menu/core/menu.context';
import { ConfigService, SettingsService } from '@menu/core/services';

declare global {
  interface String {
    toLowerUnderscoreCase(): string;
  }
}

// TODO: revise the approach here
require('@menu/core/lib/lower-underscore-case');

type MenuAppProps = {
  userConfig?: { culture: string; site?: string };
  location?: any;
  isAuthorized: boolean;
  inbox?: Inbox;
  changeDynamicMenuHeight?: (value: boolean) => void;
};

const TopMenu = observer((props: MenuAppProps) => {
  const [context, setContext] = useState<MenuContext | undefined>(undefined);
  const [locale, setLocale] = useState<string | undefined>(undefined);
  const [siteName, setSiteName] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  const { userConfig, location, isAuthorized, inbox, changeDynamicMenuHeight } = props;

  const initialize = async () => {
    const { culture, site = '' } = userConfig || getSiteAndCulture();

    setLocale(culture);
    setSiteName(site);

    const service = new ConfigService();
    const countriesTask = service.getCountries(site);
    const configTask = service.getConfig(culture, site);

    const countries = await countriesTask;
    const config = await configTask;

    if (!config) {
      console.warn('Menu config is null');
      return;
    }

    config.showAsMainSite = true;
    config.showAppBanner = true;

    //Redundant logic in case decision to make showAppBanner togglable with menu configs
    if (changeDynamicMenuHeight) {
      changeDynamicMenuHeight(config.showAppBanner || !!config.dynamicHeaderSpacing);
    }

    const { country, countryCulture } = getCountryAndCulture(countries, culture);

    const menuContextOptions: MenuContextOptions = {
      config,
      siteName,
      countries,
      country,
      culture: countryCulture,
      skipAuthValidation: true,
    };

    const menuContext = new MenuContext(menuContextOptions);

    const settings = await new SettingsService().getSettings(menuContext.countryCode, window.location.pathname);

    menuContext.updateSettings(settings);
    menuContext.updateIsLoggedIn(isAuthorized);

    if (inboxCache.isCached) {
      menuContext.updateInbox(inboxCache.value);
    }

    const setIsMobile = () => {
      const isMobile = window.innerWidth <= maxMobileWidth;

      if (!menuContext.isMobile && isMobile) {
        menuContext.updateDisplayMobileNav(false);
      }

      menuContext.updateIsMobile(isMobile);
    };

    setIsMobile();

    setContext(menuContext);

    window.addEventListener('resize', setIsMobile);
  };

  useEffect(() => {
    if (userConfig && userConfig.culture !== locale) {
      setLocale(userConfig.culture);
      setIsInitialized(false);
    }

    if (userConfig && userConfig.site !== siteName) {
      setSiteName(userConfig.site || '');
      setIsInitialized(false);
    }

    if (isInitialized) {
      return;
    }

    setIsInitialized(true);

    const load = async () => {
      initialize();
    };

    load();
  }, [isAuthorized, userConfig]);

  useEffect(() => {
    context?.updateLocation(location);
  }, [location]);

  useEffect(() => {
    if (inbox) {
      context?.updateInbox(inbox);
    }
  }, [inbox]);

  return !context ? (
    <Spinner />
  ) : (
    <header>
      <div id="root">
        <Menu context={context} />
      </div>
    </header>
  );
});

export default TopMenu;